import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import MainLayout from '../../components/Layout/MainLayout';
import { Search, Filter, Mail, Phone, Calendar, ShoppingBag, DollarSign, X, MoreVertical, Ban, UserCheck } from 'lucide-react';
import './Customers.css';

const Customers = () => {
  const [customers, setCustomers] = useState([
    { 
      id: 1, 
      name: 'Rahul Sharma', 
      email: 'rahul@example.com', 
      phone: '+91 98765 43210', 
      orders: 5, 
      spent: 2450.00, 
      status: 'Active',
      joined: 'Jan 12, 2026',
      avatar: 'RS'
    },
    { 
      id: 2, 
      name: 'Priya Patel', 
      email: 'priya@example.com', 
      phone: '+91 87654 32109', 
      orders: 3, 
      spent: 1200.00, 
      status: 'Active',
      joined: 'Feb 20, 2026',
      avatar: 'PP'
    },
    { 
      id: 3, 
      name: 'Amit Singh', 
      email: 'amit@example.com', 
      phone: '+91 76543 21098', 
      orders: 1, 
      spent: 240.00, 
      status: 'Inactive',
      joined: 'Mar 05, 2026',
      avatar: 'AS'
    },
    { 
      id: 4, 
      name: 'Sneha Gupta', 
      email: 'sneha@example.com', 
      phone: '+91 65432 10987', 
      orders: 8, 
      spent: 5600.00, 
      status: 'Active',
      joined: 'Apr 15, 2026',
      avatar: 'SG'
    },
  ]);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenDetails = (customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  const toggleStatus = (id) => {
    setCustomers(customers.map(c => 
      c.id === id ? { ...c, status: c.status === 'Active' ? 'Blocked' : 'Active' } : c
    ));
    if (selectedCustomer && selectedCustomer.id === id) {
      setSelectedCustomer({ ...selectedCustomer, status: selectedCustomer.status === 'Active' ? 'Blocked' : 'Active' });
    }
  };

  return (
    <MainLayout title="Customer Management">
      <div className="customers-container fade-in">
        <div className="customers-header">
          <div className="search-filters">
            <div className="search-input">
              <Search size={18} />
              <input type="text" placeholder="Search by Name or Email..." />
            </div>
            <button className="filter-btn">
              <Filter size={18} />
              <span>Filters</span>
            </button>
          </div>
        </div>

        <div className="customers-card">
          <div className="table-responsive">
            <table className="customers-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contact Info</th>
                  <th>Total Orders</th>
                  <th>Total Spent</th>
                  <th>Joined Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td className="customer-cell">
                      <div className="avatar">{customer.avatar}</div>
                      <div className="customer-info">
                        <span className="customer-name">{customer.name}</span>
                        <span className="customer-id">ID: #{customer.id}</span>
                      </div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <span><Mail size={14} /> {customer.email}</span>
                        <span><Phone size={14} /> {customer.phone}</span>
                      </div>
                    </td>
                    <td className="center-text">{customer.orders}</td>
                    <td className="spent-amount">${customer.spent.toFixed(2)}</td>
                    <td>{customer.joined}</td>
                    <td>
                      <span className={`status-pill ${customer.status.toLowerCase()}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="view-btn" onClick={() => handleOpenDetails(customer)}>
                          Details
                        </button>
                        <button 
                          className={`status-toggle-btn ${customer.status === 'Active' ? 'block' : 'unblock'}`}
                          onClick={() => toggleStatus(customer.id)}
                          title={customer.status === 'Active' ? 'Block Customer' : 'Unblock Customer'}
                        >
                          {customer.status === 'Active' ? <Ban size={18} /> : <UserCheck size={18} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Customer Detail Modal */}
      {isModalOpen && selectedCustomer && createPortal(
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content customer-modal fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customer Profile</h2>
              <button className="close-modal" type="button" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
            </div>

            <div className="customer-profile-header">
              <div className="profile-avatar large">{selectedCustomer.avatar}</div>
              <div className="profile-info">
                <h1>{selectedCustomer.name}</h1>
                <p>{selectedCustomer.email}</p>
                <span className={`status-pill ${selectedCustomer.status.toLowerCase()}`}>{selectedCustomer.status}</span>
              </div>
            </div>

            <div className="customer-stats-grid">
              <div className="cust-stat-card">
                <ShoppingBag size={20} className="stat-icon" />
                <div className="stat-details">
                  <p>Total Orders</p>
                  <h3>{selectedCustomer.orders}</h3>
                </div>
              </div>
              <div className="cust-stat-card">
                <DollarSign size={20} className="stat-icon" />
                <div className="stat-details">
                  <p>Total Spent</p>
                  <h3>${selectedCustomer.spent.toFixed(2)}</h3>
                </div>
              </div>
              <div className="cust-stat-card">
                <Calendar size={20} className="stat-icon" />
                <div className="stat-details">
                  <p>Joined On</p>
                  <h3>{selectedCustomer.joined}</h3>
                </div>
              </div>
            </div>

            <div className="customer-details-section">
              <h3>Contact Details</h3>
              <div className="contact-list">
                <div className="contact-item">
                  <Mail size={18} />
                  <span>{selectedCustomer.email}</span>
                </div>
                <div className="contact-item">
                  <Phone size={18} />
                  <span>{selectedCustomer.phone}</span>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className={`action-btn-large ${selectedCustomer.status === 'Active' ? 'danger' : 'success'}`}
                onClick={() => toggleStatus(selectedCustomer.id)}
              >
                {selectedCustomer.status === 'Active' ? 'Block Customer' : 'Unblock Customer'}
              </button>
              <button className="action-btn-large secondary" onClick={() => setIsModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </MainLayout>
  );
};

export default Customers;
